/**
 * The main transaction api handler
 *
 * @author Lemuel Raganas
 */
import axios from "axios";
import * as lob from "lodash/object";
import MainStub from "@/libs/data-api/MainStub";
import GetOption from "@/libs/data-api/GetOption";
import DataPoint from "@/libs/world-cup/DataPoint";
import v from "voca";

class DataApi {
  private year: number = 0;
  private host: string = "https://www.channelnewsasia.com";
  private language: string;
  private segment = "/static/source";
  private timeout: number;
  private checksum: boolean;
  private endpoints: any = {
    externalTranslation: undefined, // This a provision intended to be overriden so users can provide their own translation urls. Must conform to key-value mapping json,
    checksum: "/{year}/checksum.json?t={timestamp}",
    labels: "/{year}/labels-{lang}.json?t={timestamp}",
    worldCupMatches: "/{year}/{lang}/MA1_Live.json?t={timestamp}",
    pointsTable: "/{year}/{lang}/TM2.json?t={timestamp}",
  };

  constructor(dataPoint: DataPoint) {
    this.timeout = dataPoint.connectionTimeout || 0;
    this.year = dataPoint.year;
    this.host = dataPoint.host || this.host;
    this.language = dataPoint.language || "en";
    this.checksum =
      dataPoint.checksum === undefined ? true : dataPoint.checksum;

    // Allow endpoint override
    this.segment =
      dataPoint.segment !== undefined ? dataPoint.segment : this.segment;

    this.endpoints.labels = lob.get(
      dataPoint.api,
      "labels",
      this.endpoints.labels
    );

    this.endpoints.externalTranslation = lob.get(
      dataPoint.api,
      "externalTranslation",
      this.endpoints.externalTranslation
    );

    this.endpoints.worldCupMatches = lob.get(
      dataPoint.api,
      "worldCupMatches",
      this.endpoints.worldCupMatches
    );

    this.endpoints.pointsTable = lob.get(
      dataPoint.api,
      "pointsTable",
      this.endpoints.pointsTable
    );
  }

  private getApiUrl(api: string, options?: GetOption) {
    const jsonSegment = lob.get(this.endpoints, api, undefined);

    if (jsonSegment === undefined || jsonSegment === null) {
      return undefined;
    }

    let template = "";

    // Identify incase the specified api url is absolute
    if (
      v.startsWith(jsonSegment, "http://") ||
      v.startsWith(jsonSegment, "https://")
    ) {
      template = jsonSegment;
    } else {
      template = this.host + this.segment + jsonSegment;
    }

    let url = template.replace("{year}", String(this.year));
    const language = lob.get(options, "language", this.language);
    const lang = lob.get(options, "lang", language);

    if (lang !== null && lang !== undefined) {
      url = url.replace("{lang}", lang);
    }

    const timestamp = Math.floor(Date.now() / 1000 / 30);
    // let time = Date.now();
    const  timezoneCustome = Intl.DateTimeFormat().resolvedOptions().timeZone.replace("/","");
    url = url.replace("{timestamp}", timestamp + "_" + timezoneCustome);

    return url;
  }

  private strReplaceAll(subject: string, search: string, replacement: string) {
    return subject.split(search).join(replacement);
  }

  public hasApi(api: string) {
    const url = lob.get(this.endpoints, api, undefined);

    if (url !== undefined && url !== null) {
      return true;
    }

    return false;
  }

  public get(api: string, options?: GetOption) {
    const url = this.getApiUrl(api, options);

    if (url === undefined || url === null) {
      return undefined;
    }

    return axios({
      url: url,
      timeout: this.timeout,
    });
  }

  public shouldVerifyHash() {
    return this.checksum;
  }
}

export default DataApi;
